// Domain routing: classifies a research query into a domain taxonomy and
// returns site filters + recommended sub-agent personas. Uses a fast LLM call
// with a graceful keyword-based fallback so the engine never crashes when
// the gateway is down.

import { llmCall } from "./api";

export type ResearchDomain =
  | "physics"
  | "biology"
  | "chemistry"
  | "cs_ai"
  | "cs_systems"
  | "medicine"
  | "economics"
  | "law"
  | "history"
  | "philosophy"
  | "general";

export interface DomainClassification {
  primary: ResearchDomain;
  secondary: ResearchDomain;
  confidence: number; // 0..1 for primary
  rationale: string;
  source: "llm" | "heuristic";
}

// Authoritative source map: domain -> preferred site filters for web search.
export const DOMAIN_SOURCES: Record<ResearchDomain, string[]> = {
  physics: ["arxiv.org", "nasa.gov", "aps.org", "iopscience.iop.org"],
  biology: ["pubmed.ncbi.nlm.nih.gov", "biorxiv.org", "nature.com", "cell.com"],
  chemistry: ["pubs.acs.org", "rsc.org", "chemrxiv.org"],
  cs_ai: ["arxiv.org", "openreview.net", "anthropic.com", "openai.com", "deepmind.google"],
  cs_systems: ["arxiv.org", "usenix.org", "acm.org", "ieeexplore.ieee.org"],
  medicine: ["pubmed.ncbi.nlm.nih.gov", "nejm.org", "thelancet.com", "bmj.com", "who.int"],
  economics: ["nber.org", "imf.org", "worldbank.org", "ssrn.com"],
  law: ["courtlistener.com", "supremecourt.gov", "law.cornell.edu", "justia.com"],
  history: ["jstor.org", "archive.org", "loc.gov"],
  philosophy: ["plato.stanford.edu", "philpapers.org", "jstor.org"],
  general: [],
};

// Domain-specific personas the planner should spawn (replaces hardcoded
// "Reproducibility evidence" / "Adversarial cases against …" fragments).
export const DOMAIN_PERSONAS: Record<ResearchDomain, string[]> = {
  physics: ["Theoretical framework", "Experimental evidence", "Open problems", "Cross-discipline implications"],
  biology: ["Mechanism", "Clinical/ecological evidence", "Replication studies", "Open controversies"],
  chemistry: ["Reaction pathway", "Synthesis evidence", "Industrial applications", "Safety/regulatory"],
  cs_ai: ["Technical approach", "Empirical results", "Adversarial robustness", "Ethical/societal impact", "Reproducibility"],
  cs_systems: ["Architecture", "Performance benchmarks", "Failure modes", "Adoption tradeoffs"],
  medicine: ["Clinical evidence", "Mechanism of action", "Adverse effects", "Guideline recommendations"],
  economics: ["Theoretical model", "Empirical evidence", "Policy implications", "Counter-arguments"],
  law: ["Statutory basis", "Case law", "Jurisdictional variance", "Scholarly critique"],
  history: ["Primary sources", "Historiographic debate", "Material evidence", "Counterfactuals"],
  philosophy: ["Argument structure", "Counter-arguments", "Historical lineage", "Modern interpretations"],
  general: ["Definitions", "Main approaches", "Evidence", "Limitations", "Open questions"],
};

const KEYWORDS: Array<[ResearchDomain, RegExp]> = [
  ["cs_ai", /\b(llm|alignment|transformer|rlhf|deep learning|neural net|gpt|claude|gemini|agent|attention|embedding|fine[- ]?tun|prompt)/i],
  ["cs_systems", /\b(distributed|kubernetes|kernel|database|sql|http|tcp|microservice|latency|throughput|architecture)/i],
  ["physics", /\b(quantum|relativity|particle|photon|cosmolog|astrophys|black ?hole|gravitation|thermodynamic)/i],
  ["biology", /\b(gene|crispr|protein|cell|enzyme|microbio|ecolog|evolution|biolog|genom)/i],
  ["chemistry", /\b(catalyst|molecule|reaction|polymer|synthesis|organic chem|inorganic|electrochem)/i],
  ["medicine", /\b(clinical|trial|patient|diagnos|therap|disease|drug|vaccin|epidemio|cancer|cardio)/i],
  ["economics", /\b(monetary|fiscal|inflation|gdp|market|equilibrium|game theor|trade|tariff|labor)/i],
  ["law", /\b(statute|court|jurisdict|constitution|tort|contract law|precedent|litigation|regulator)/i],
  ["history", /\b(century|empire|dynasty|war of |revolution|medieval|ancient|colonial|reformation)/i],
  ["philosophy", /\b(epistemo|metaphysic|ontolog|ethics|moral philosophy|phenomenolog|kant|hegel|aristot)/i],
];

export function heuristicDomain(query: string): DomainClassification {
  const scores = new Map<ResearchDomain, number>();
  for (const [d, re] of KEYWORDS) {
    const m = query.match(re);
    if (m) scores.set(d, (scores.get(d) ?? 0) + m.length);
  }
  if (!scores.size) {
    return { primary: "general", secondary: "general", confidence: 0.4, rationale: "no keyword match", source: "heuristic" };
  }
  const sorted = Array.from(scores.entries()).sort((a, b) => b[1] - a[1]);
  const primary = sorted[0][0];
  const secondary = sorted[1]?.[0] ?? primary;
  const total = sorted.reduce((s, [, n]) => s + n, 0);
  return { primary, secondary, confidence: Math.min(0.85, 0.55 + sorted[0][1] / Math.max(total, 1) * 0.3), rationale: "keyword heuristic", source: "heuristic" };
}

export async function classifyQueryDomain(query: string, signal?: AbortSignal): Promise<DomainClassification> {
  const fallback = heuristicDomain(query);
  if (signal?.aborted) return fallback;

  try {
    const r = await llmCall({
      provider: "lovable",
      model: "google/gemini-2.5-flash-lite",
      max_tokens: 200,
      temperature: 0,
      json: true,
      messages: [
        {
          role: "system",
          content:
            "Classify the research query into a domain taxonomy. Return strict JSON: " +
            '{"primary":"<domain>","secondary":"<domain>","confidence":0..1,"rationale":"<one short sentence>"}. ' +
            'Allowed domains: physics, biology, chemistry, cs_ai, cs_systems, medicine, economics, law, history, philosophy, general.',
        },
        { role: "user", content: query.slice(0, 1500) },
      ],
    });
    if (!r.ok) return fallback;
    const m = r.data.content.match(/\{[\s\S]*\}/);
    if (!m) return fallback;
    const parsed = JSON.parse(m[0]) as { primary?: string; secondary?: string; confidence?: number; rationale?: string };
    const allowed = Object.keys(DOMAIN_SOURCES) as ResearchDomain[];
    const primary = (allowed.includes(parsed.primary as ResearchDomain) ? parsed.primary : "general") as ResearchDomain;
    const secondary = (allowed.includes(parsed.secondary as ResearchDomain) ? parsed.secondary : primary) as ResearchDomain;
    return {
      primary,
      secondary,
      confidence: typeof parsed.confidence === "number" ? Math.max(0, Math.min(1, parsed.confidence)) : 0.7,
      rationale: parsed.rationale ?? "llm classification",
      source: "llm",
    };
  } catch {
    return fallback;
  }
}

/** Build a Tavily-style query string with site: filters derived from domains. */
export function siteFilteredQuery(query: string, domains: ResearchDomain[], maxSites = 5): string {
  const sites = Array.from(new Set(domains.flatMap((d) => DOMAIN_SOURCES[d]))).slice(0, maxSites);
  if (!sites.length) return query;
  const filter = sites.map((s) => `site:${s}`).join(" OR ");
  return `${query} (${filter})`;
}
